import { type ReactNode } from 'react';
import { Pressable, Text as RNText, View } from 'react-native';

import { cn } from '@/components/ui/cn';

import { claimantsEditingLabel } from './claimantsLabel';
import { type FieldClaimDisplay } from './fieldClaimDisplay';

/**
 * The slice of a field's presence wiring the claim frame renders. Editor call
 * sites pass their `ClaimableField` whole (it satisfies this structurally);
 * spectator call sites pass claims alone — nothing blocks for them.
 */
export interface FieldClaimState {
  /** Every displayed claim on the field, earliest first; empty if unclaimed. */
  claims: FieldClaimDisplay[];
  /** Whether the claim currently blocks local editing. */
  blocked?: boolean;
  /** Raise the blocked-tap toast; `open` mounts a draft input first. */
  blockedPress?(open?: () => void): void;
}

/**
 * Visual shell around a claimable field. When collaborators hold focus claims
 * it draws the earliest claimant's presence-colored border and every
 * claimant's name in their own color; when additionally blocked (the local
 * user is neither co-editing nor overriding) it overlays a pressable that
 * swallows taps and reports them via `field.blockedPress`, so the input below
 * can't be focused. Purely visual — the parent still sets the input read-only.
 */
export function FieldClaimFrame({
  field,
  onBlockedPress,
  children,
  className,
}: {
  field?: FieldClaimState;
  /** Override the blocked-tap handler (defaults to `field.blockedPress()`). */
  onBlockedPress?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const claims = field?.claims ?? [];
  const primary = claims[0];
  return (
    <View className={cn('relative', className)}>
      {children}
      {primary ? (
        <>
          <View
            pointerEvents="none"
            className="absolute -inset-x-1.5 -inset-y-0.5 rounded-md border-[1.5px]"
            style={{ borderColor: primary.color }}
          />
          <View
            pointerEvents="none"
            className="absolute -top-2.5 right-0 flex-row gap-1 rounded-sm bg-card px-1"
          >
            {claims.map((claim, i) => (
              <RNText
                key={`${claim.name}-${i}`}
                className="font-accent text-[9px]"
                style={{ color: claim.color }}
              >
                {claim.name}
              </RNText>
            ))}
          </View>
        </>
      ) : null}
      {primary && field?.blocked ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={claimantsEditingLabel(claims, 'field')}
          onPress={onBlockedPress ?? (() => field.blockedPress?.())}
          className="absolute -inset-x-1.5 -inset-y-0.5"
        />
      ) : null}
    </View>
  );
}
